import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ExternalLink, Star, X } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { inputBase } from '@/lib/form-styles'
import {
  FeldStatus,
  Feldgruppe,
  mitStand,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
  type Status,
} from '@/lib/speichern'
import { changeWorkStepStatus, fetchJournal, releaseWorkSteps, revokeWorkStepRelease } from '../../api/projects-phase-d.api'
import { deleteAssignment, endAssignment, fetchStepAssignments } from '../../api/work-cockpit.api'
import { WORK_STEP_STATUS_LABELS, alsZeitpunkt } from '../../project-labels'
import type { JournalEntry } from '../../types/phase-d.types'
import type { CockpitCard, StepAssignment } from '../../types/cockpit.types'
import { ProjectFilesPanel } from '../ProjectFilesPanel'
import { formatStunden, kurzDatum } from './cockpit-labels'
import { frage } from '@/lib/dialoge'

type Reiter = 'step' | 'assign' | 'journal'

/**
 * AP-35 Detailblatt 11 4.2 — **die Detailansicht einer Karte.**
 *
 * Seitenleiste rechts, kein Seitenwechsel. Die Aktionen gehen über die Wege,
 * die es schon gibt: Freigabe und Lebenslauf über den Arbeitsschritt, die
 * Einteilung über ihre eigenen Endpunkte. Das Cockpit hat keinen eigenen
 * Status (Blatt 11 6.). Die Pfeile blättern durch die gefilterten Karten.
 */
export function CockpitDetailPanel({
  karte,
  onClose,
  onPrev,
  onNext,
  onChanged,
  onEinteilen,
}: {
  karte: CockpitCard
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  onChanged: () => void
  onEinteilen: () => void
}) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const darfFreigeben = erfuelltRecht('projects.approve.execution', user?.permissions)
  const darfStatus = erfuelltRecht('projects.workstep.manage', user?.permissions) || erfuelltRecht('projects.position.manage', user?.permissions)
  const darfEinteilen = erfuelltRecht('projects.assign.manage', user?.permissions)

  const [reiter, setReiter] = useState<Reiter>('step')
  const [einteilungen, setEinteilungen] = useState<StepAssignment[]>([])
  const [journal, setJournal] = useState<JournalEntry[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    setFehler(null)
    if (reiter === 'assign') {
      fetchStepAssignments(karte.stepId).then(setEinteilungen).catch((e) => setFehler(apiErrorText(e, 'Laden fehlgeschlagen.')))
    }
    if (reiter === 'journal') {
      fetchJournal(karte.project.id, { positionId: karte.position.id, limit: 50 })
        .then((r) => setJournal(r.data))
        .catch((e) => setFehler(apiErrorText(e, 'Laden fehlgeschlagen.')))
    }
  }, [reiter, karte.stepId, karte.project.id, karte.position.id])

  async function handlung(f: () => Promise<unknown>) {
    setLaeuft(true)
    setFehler(null)
    try {
      await f()
      onChanged()
      if (reiter === 'assign') setEinteilungen(await fetchStepAssignments(karte.stepId))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  const status = karte.executionStatus
  const knopf = 'rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-40'

  return (
    <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-background shadow-xl" aria-label="Arbeitsschritt">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" onClick={onPrev} disabled={!onPrev} aria-label="vorige Karte">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button type="button" className="rounded p-1 hover:bg-muted disabled:opacity-30" onClick={onNext} disabled={!onNext} aria-label="nächste Karte">
          <ChevronRight className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-muted-foreground">
            {karte.project.projectNumber} {karte.project.name}
          </div>
          <div className="truncate font-semibold">
            <span className="font-mono text-muted-foreground">{karte.number}</span> {karte.activityName}
          </div>
        </div>
        <button type="button" className="rounded p-1 hover:bg-muted" onClick={onClose} aria-label="schließen">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-2">
        {darfFreigeben && ['PLANNED', 'PAUSED'].includes(status) && (
          <button type="button" className={knopf} disabled={laeuft} onClick={() => void handlung(() => releaseWorkSteps([karte.stepId]))}>
            Freigeben
          </button>
        )}
        {darfFreigeben && ['READY', 'ASSIGNED', 'IN_PROGRESS'].includes(status) && (
          <button
            type="button"
            className={knopf}
            disabled={laeuft}
            onClick={async () => {
              const grund = await frage('Begründung für den Widerruf')
              if (grund?.trim()) void handlung(() => revokeWorkStepRelease(karte.stepId, grund.trim()))
            }}
          >
            Widerrufen
          </button>
        )}
        {darfStatus && ['READY', 'ASSIGNED'].includes(status) && (
          <button type="button" className={knopf} disabled={laeuft} onClick={() => void handlung(() => changeWorkStepStatus(karte.stepId, { to: 'IN_PROGRESS' }))}>
            Starten
          </button>
        )}
        {darfStatus && status === 'IN_PROGRESS' && (
          <button
            type="button"
            className={knopf}
            disabled={laeuft}
            onClick={async () => {
              const grund = await frage('Warum pausiert der Schritt?') ?? ''
              void handlung(() => changeWorkStepStatus(karte.stepId, { to: 'PAUSED', note: grund.trim() || undefined }))
            }}
          >
            Pausieren
          </button>
        )}
        {darfStatus && ['READY', 'ASSIGNED', 'IN_PROGRESS'].includes(status) && (
          <button type="button" className={knopf} disabled={laeuft} onClick={() => void handlung(() => changeWorkStepStatus(karte.stepId, { to: 'DONE' }))}>
            Fertig
          </button>
        )}
        {darfEinteilen && (
          <button type="button" className={knopf} onClick={onEinteilen}>
            Einteilen
          </button>
        )}
        <button type="button" className={`${knopf} ml-auto flex items-center gap-1`} onClick={() => navigate(`/projects/${karte.project.id}?tab=positions&position=${karte.position.id}`)}>
          Position öffnen <ExternalLink className="h-3 w-3" />
        </button>
      </div>

      <div className="flex gap-1 border-b border-border px-4">
        {(
          [
            ['step', 'Schritt'],
            ['assign', 'Einteilung'],
            ['journal', 'Journal'],
          ] as [Reiter, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setReiter(key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${reiter === key ? 'border-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {fehler && <p className="mx-4 mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

      <div className="flex-1 overflow-y-auto px-4 py-3 text-sm">
        {reiter === 'step' && (
          <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-2">
            <dt className="text-muted-foreground">Position</dt>
            <dd>
              {karte.position.number} {karte.position.name}
            </dd>
            <dt className="text-muted-foreground">Status</dt>
            <dd>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${WORK_STEP_STATUS_LABELS[status]?.class ?? ''}`}>
                {WORK_STEP_STATUS_LABELS[status]?.label ?? status}
              </span>
              {karte.releaseNote && <span className="ml-2 text-xs text-muted-foreground">{karte.releaseNote}</span>}
            </dd>
            <dt className="text-muted-foreground">Soll / Ist</dt>
            <dd className="tabular-nums">
              {formatStunden(karte.plannedHoursTotal)} h / {formatStunden(karte.actualHours)} h
            </dd>
            <dt className="text-muted-foreground">fällig</dt>
            <dd className={karte.overdue ? 'font-medium text-destructive' : undefined}>{kurzDatum(karte.dueAt)}</dd>
            <dt className="text-muted-foreground">fixiert</dt>
            <dd>{karte.fixedAt ? `${kurzDatum(karte.fixedAt)} ${karte.fixedTime ?? ''}` : '—'}</dd>
            <dt className="text-muted-foreground">Qualifikation</dt>
            <dd>{karte.qualification?.name ?? '—'}</dd>
            <dt className="text-muted-foreground">Kunde</dt>
            <dd>{karte.project.clientName ?? '—'}</dd>
            <dt className="text-muted-foreground">zuständig</dt>
            <dd>{karte.responsibleName ?? '—'}</dd>
            {karte.description && (
              <>
                <dt className="text-muted-foreground">Beschreibung</dt>
                <dd className="whitespace-pre-wrap">{karte.description}</dd>
              </>
            )}
            <dt className="col-span-2 mt-2 font-medium">Dateien</dt>
            <dd className="col-span-2">
              <ProjectFilesPanel projectId={karte.project.id} entityType="WORK_STEP" entityId={karte.stepId} onChanged={onChanged} />
            </dd>
          </dl>
        )}

        {reiter === 'assign' && (
          <div className="space-y-2">
            {einteilungen.length === 0 && <p className="text-muted-foreground">Noch niemand eingeteilt.</p>}
            {einteilungen.map((e) => (
              <EinteilungZeile
                key={e.id}
                e={e}
                darf={darfEinteilen}
                laeuft={laeuft}
                onGespeichert={onChanged}
                onEnd={() => void handlung(() => endAssignment(e.id))}
                onDelete={() => void handlung(() => deleteAssignment(e.id))}
              />
            ))}
            {darfEinteilen && (
              <button type="button" className={knopf} onClick={onEinteilen}>
                + Mitarbeiter einteilen
              </button>
            )}
          </div>
        )}

        {reiter === 'journal' && (
          <ul className="space-y-2">
            {journal.length === 0 && <li className="text-muted-foreground">Keine Einträge zu dieser Position.</li>}
            {journal.map((j) => (
              <li key={j.id} className={`rounded-md border border-border px-3 py-2 ${j.isSystem ? 'bg-muted/40 text-muted-foreground' : ''}`}>
                <div className="text-xs text-muted-foreground">
                  {alsZeitpunkt(j.occurredAt)} · {j.authorName ?? 'System'}
                </div>
                <div className="whitespace-pre-wrap">{j.text}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}

/**
 * Gesperrt sieht das Feld wie Text aus (AP-36 4.1) — wer nicht einteilen darf,
 * liest die Einteilung trotzdem, ohne zweite Darstellung.
 */
const einteilungsFeld = `${inputBase} py-1 text-xs disabled:cursor-default disabled:border-transparent disabled:bg-transparent disabled:px-0 disabled:text-foreground disabled:opacity-100 aria-[invalid=true]:border-destructive`

/** Ein Feld samt Zustand: Häkchen daneben, ein Fehler bricht in die nächste Zeile um. */
function Eingabe({ status, children }: { status: Status; children: ReactNode }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {children}
      <FeldStatus status={status} />
    </span>
  )
}

/**
 * Von und Bis sind nur zusammen gültig (Bis nicht vor Von) und gehen deshalb
 * als **Gruppe** hinaus — beim Verlassen der Gruppe, nicht beim Wechsel
 * zwischen den beiden Feldern. Die Hooks laufen hier, weil sie den Kontext der
 * umgebenden `Feldgruppe` brauchen.
 */
function ZeitraumFelder({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  const von = useFeld(stelle, 'startsOn', { art: 'datum', label: 'von', pflicht: true, gesperrt })
  const bis = useFeld(stelle, 'endsOn', { art: 'datum', label: 'bis', pflicht: true, gesperrt })
  return (
    <>
      <input type="date" aria-label="von" className={`${einteilungsFeld} w-36`} {...von.props} />
      <input type="date" aria-label="bis" min={von.wert ?? undefined} className={`${einteilungsFeld} w-36`} {...bis.props} />
    </>
  )
}

/**
 * Eine Einteilung — AP-36 Fall 1: Die Felder stehen immer da und speichern beim
 * Verlassen, es gibt weder „ändern" noch einen Speichern-Knopf. „Ab heute nicht
 * mehr" und „entfernen" bleiben Handlungen und behalten ihr Verb.
 */
function EinteilungZeile({
  e,
  darf,
  laeuft,
  onGespeichert,
  onEnd,
  onDelete,
}: {
  e: StepAssignment
  darf: boolean
  laeuft: boolean
  onGespeichert: () => void
  onEnd: () => void
  onDelete: () => void
}) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `assignment:${e.id}`,
    bezeichnung: `Einteilung ${e.userName}`,
    daten: e as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const antwort = await api.patch<{ data: { assignment: StepAssignment | null } }>(`/projects/assignments/${e.id}`, patch, mitStand(stand))
      // Die Karte auf der Tafel trägt ihre Einteilungen — nach dem Schreiben ist sie veraltet.
      onGespeichert()
      return (antwort.data.data.assignment ?? {}) as unknown as Datensatz
    },
    keepalive: { url: `/projects/assignments/${e.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>

  const stunden = useFeld<number | null>(stelle, 'plannedHours', {
    art: 'zahl',
    stellen: 2,
    label: 'Stunden',
    gesperrt: !darf,
    pruefe: (w) => (w != null && w < 0 ? 'Keine negative Stundenzahl' : null),
  })
  const notiz = useFeld(stelle, 'note', { label: 'Anmerkung', gesperrt: !darf })
  const fuehrt = useFeld<boolean>(stelle, 'isLead', { art: 'schalter', label: 'führt', gesperrt: !darf })

  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="flex items-center gap-2">
        {fuehrt.wert && <Star className="h-3.5 w-3.5 text-primary" aria-label="führt" />}
        <span className="font-medium">{e.userName}</span>
        {darf && (
          <span className="ml-auto flex gap-2 text-xs">
            <button type="button" className="text-primary hover:underline" disabled={laeuft} onClick={onEnd}>
              ab heute nicht mehr
            </button>
            <button type="button" className="text-destructive hover:underline" disabled={laeuft} onClick={onDelete}>
              entfernen
            </button>
          </span>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Feldgruppe
          stelle={stelle}
          label="Zeitraum"
          pruefe={(w) => (w.startsOn && w.endsOn && String(w.endsOn) < String(w.startsOn) ? 'Bis darf nicht vor Von liegen.' : null)}
          className="flex flex-wrap items-center gap-2"
        >
          <ZeitraumFelder stelle={stelle} gesperrt={!darf} />
        </Feldgruppe>
        <Eingabe status={stunden}>
          <input aria-label="Stunden" className={`${einteilungsFeld} w-16 text-right`} {...stunden.props} />
          <span className="text-xs text-muted-foreground">h</span>
        </Eingabe>
        <Eingabe status={notiz}>
          <input aria-label="Anmerkung" placeholder="Notiz" maxLength={200} className={`${einteilungsFeld} w-44`} {...notiz.props} />
        </Eingabe>
        <label className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          <input type="checkbox" {...fuehrt.schalter} />
          führt
          <FeldStatus status={fuehrt} />
        </label>
      </div>
    </div>
  )
}
